import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, Award, Languages, MapPin, Phone, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingBookButton from "@/components/FloatingBookButton";
import { Toaster } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SITE_URL = "https://bc-ice.ca";

const setMeta = (name, content, attr = "name") => {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setCanonical = (href) => {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const setJsonLd = (data) => {
  const id = "practitioner-jsonld";
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

const PractitionerPage = () => {
  const { slug } = useParams();
  const [member, setMember] = useState(null);
  const [status, setStatus] = useState("loading");
  const canonicalUrl = `${SITE_URL}/team/${slug}`;

  useEffect(() => {
    let cancelled = false;
    const fetchMember = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/team/${slug}`);
        if (!cancelled) {
          setMember(res.data);
          setStatus("ready");
        }
      } catch (err) {
        if (!cancelled) setStatus("notfound");
      }
    };
    fetchMember();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Restore defaults on unmount
  useEffect(() => {
    const originalTitle = document.title;
    return () => {
      document.title = originalTitle;
      setCanonical(SITE_URL + "/");
      const el = document.getElementById("practitioner-jsonld");
      if (el) el.remove();
    };
  }, []);

  useEffect(() => {
    if (!member) return;
    const title = `${member.name} - ${member.title} | BC-ICE Burnaby`;
    const description = `Book with ${member.name}, ${member.title} at BC-ICE in Burnaby. ${member.bio.slice(0, 140)}`;
    document.title = title;
    setMeta("description", description);
    setMeta("keywords", `${member.name}, ${member.title}, ${member.specialty || ""}, physiotherapy Burnaby, BC-ICE`);
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:type", "profile", "property");
    setMeta("og:url", canonicalUrl, "property");
    setMeta("og:image", `${SITE_URL}${member.image}`, "property");
    setMeta("twitter:title", title, "property");
    setMeta("twitter:description", description, "property");
    setMeta("twitter:image", `${SITE_URL}${member.image}`, "property");
    setCanonical(canonicalUrl);

    setJsonLd({
      "@context": "https://schema.org",
      "@type": "Person",
      name: member.name,
      jobTitle: member.title,
      image: `${SITE_URL}${member.image}`,
      url: canonicalUrl,
      knowsLanguage: member.languages,
      description: member.bio,
      worksFor: {
        "@type": "MedicalBusiness",
        name: "BC-ICE (BC Injury Care & Exercise)",
        url: SITE_URL,
        telephone: "+1-604-553-4000",
        address: {
          "@type": "PostalAddress",
          streetAddress: "7885 6th St #201",
          addressLocality: "Burnaby",
          addressRegion: "BC",
          postalCode: "V3N 3N4",
          addressCountry: "CA",
        },
      },
    });
  }, [member, canonicalUrl]);

  if (status === "loading") {
    return (
      <main className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-32 pb-24 text-center text-gray-500" data-testid="practitioner-loading">
          Loading practitioner profile...
        </div>
        <Footer />
      </main>
    );
  }

  if (status === "notfound" || !member) {
    return (
      <main className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-32 pb-24 max-w-3xl mx-auto px-6 text-center" data-testid="practitioner-notfound">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Practitioner not found</h1>
          <p className="text-gray-600 mb-8">
            The team member you&apos;re looking for doesn&apos;t exist or may have moved.
          </p>
          <Link to="/#team">
            <Button className="bg-[#0F4C3A] hover:bg-[#1A6B52] text-white rounded-full px-8 py-4 font-semibold">
              Back to Our Team
            </Button>
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-white" data-testid="practitioner-page">
      <Navbar />

      <section className="pt-28 pb-16 gradient-hero">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/#team"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#0F4C3A] hover:text-[#1A6B52] mb-8"
            data-testid="practitioner-back"
          >
            <ArrowLeft className="w-4 h-4" /> Back to team
          </Link>

          <div className="grid lg:grid-cols-5 gap-10 items-start">
            {/* Image */}
            <div className="lg:col-span-2 animate-fade-up stagger-1">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src={member.image}
                  alt={`${member.name}, ${member.title} at BC-ICE Burnaby`}
                  className="w-full aspect-[4/5] object-cover"
                  data-testid="practitioner-image"
                />
              </div>
            </div>

            {/* Header info */}
            <div className="lg:col-span-3 space-y-6">
              <div className="animate-fade-up stagger-2">
                <span className="inline-flex items-center gap-2 bg-[#D1FAE5] text-[#064E3B] px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider">
                  <CheckCircle className="w-3.5 h-3.5" />
                  {member.specialty || "Care Team"}
                </span>
                <h1
                  className="mt-4 text-4xl md:text-5xl font-bold text-gray-900 leading-tight"
                  data-testid="practitioner-name"
                >
                  {member.name}
                </h1>
                <p className="mt-2 text-lg text-[#0F4C3A] font-semibold">{member.title}</p>
              </div>

              <div className="flex flex-wrap gap-2 animate-fade-up stagger-3">
                {member.certifications.map((cert) => (
                  <span
                    key={cert}
                    className="inline-flex items-center gap-1 bg-white border border-[#0F4C3A]/15 text-[#0F4C3A] text-xs font-medium px-3 py-1.5 rounded-full shadow-sm"
                  >
                    <Award className="w-3 h-3" />
                    {cert}
                  </span>
                ))}
              </div>

              <div className="flex items-center gap-2 text-gray-600 animate-fade-up stagger-4">
                <Languages className="w-5 h-5 text-[#0F4C3A]" />
                <span className="text-sm">{member.languages.join(", ")}</span>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-2 animate-fade-up stagger-5">
                <a
                  href={member.booking_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="practitioner-book-btn"
                >
                  <Button className="w-full sm:w-auto bg-[#0F4C3A] hover:bg-[#1A6B52] text-white rounded-full px-8 py-6 text-base font-semibold shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1">
                    Book with {member.name.split(" ")[0]}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </a>
                <a href="#contact">
                  <Button
                    variant="outline"
                    className="w-full sm:w-auto border-2 border-[#0F4C3A] text-[#0F4C3A] hover:bg-[#0F4C3A] hover:text-white rounded-full px-8 py-6 text-base font-semibold transition-all"
                  >
                    Ask a Question
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Full bio */}
      <section className="py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
            About {member.name.split(" ")[0]}
          </h2>
          <div
            className="prose prose-lg max-w-none text-gray-700 whitespace-pre-line leading-relaxed"
            data-testid="practitioner-bio"
          >
            {member.full_bio || member.bio}
          </div>

          {/* Clinic info card */}
          <div className="mt-12 grid sm:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-gray-200 p-6 bg-[#F9FAFB]">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#0F4C3A] mt-1" />
                <div>
                  <p className="text-sm font-semibold text-gray-900">Visit BC-ICE</p>
                  <p className="text-sm text-gray-600 mt-1">
                    Unit 201 - 7885 6th Street<br />Burnaby, BC V3N 3N4
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-2xl border border-gray-200 p-6 bg-[#F9FAFB]">
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-[#0F4C3A] mt-1" />
                <div>
                  <p className="text-sm font-semibold text-gray-900">Call to book</p>
                  <a
                    href="tel:+16045534000"
                    className="text-sm text-gray-600 mt-1 hover:text-[#0F4C3A]"
                  >
                    604-553-4000
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <a
              href={member.booking_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="practitioner-book-bottom"
            >
              <Button className="bg-[#0F4C3A] hover:bg-[#1A6B52] text-white rounded-full px-10 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
                Book with {member.name.split(" ")[0]} on Jane
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingBookButton />
      <Toaster position="top-center" />
    </main>
  );
};

export default PractitionerPage;
